import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  ListItemText,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Title, useNotify, usePermissions } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import {
  STAFF_SETTINGS_EDIT,
  STAFF_SETTINGS_VIEW,
} from "@/containers/staff/shared/staff-payroll-permissions";
import { hasPermission } from "@/utils/permission-utils";
import {
  PAID_LEAVE_TYPE_OPTIONS,
  PAYROLL_DAY_BASIS_OPTIONS,
  PROFESSIONAL_TAX_PROFILE_OPTIONS,
  STAFF_SETTINGS_TOGGLE_HINTS,
  applyStandardLeavePolicyPreset,
  payrollDayBasisHelperText,
  toStaffSettingsFormState,
  toStaffSettingsPatch,
  togglePaidLeaveType,
  validateStaffSettingsForm,
  type PaidLeaveType,
  type PayrollDayBasis,
  type ProfessionalTaxProfile,
  type StaffSettingsApiPayload,
  type StaffSettingsFormState,
} from "./staff-settings-form";

const emptyState = (): StaffSettingsFormState => toStaffSettingsFormState({});

type SettingsToggleRowProps = {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
};

/** Groups a switch with its hint so parent spacing separates rows, not label-from-hint. */
function SettingsToggleRow({ label, hint, checked, disabled, onChange }: SettingsToggleRowProps) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <FormControlLabel
        sx={{ alignItems: "center", ml: 0, mr: 0 }}
        control={
          <Switch
            checked={checked}
            onChange={(_, next) => onChange(next)}
            color="primary"
            disabled={disabled}
          />
        }
        label={label}
      />
      <Typography variant="body2" color="text.secondary" sx={{ pl: 6 }}>
        {hint}
      </Typography>
    </Box>
  );
}

type SettingsSectionProps = {
  title: string;
  children: ReactNode;
};

/** Light outlined section for Payroll / Leave policy (theme tokens only). */
function SettingsSection({ title, children }: SettingsSectionProps) {
  return (
    <Box
      component="section"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="subtitle1" component="h2" fontWeight={600}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function StaffSettingsPage() {
  const notify = useNotify();
  const { permissions } = usePermissions();
  const canView =
    hasPermission(permissions, STAFF_SETTINGS_VIEW) || hasPermission(permissions, "employees.view");
  const canEdit =
    hasPermission(permissions, STAFF_SETTINGS_EDIT) || hasPermission(permissions, "employees.edit");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<StaffSettingsFormState>(emptyState);

  const load = useCallback(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    callApi({ location: "staffSettings", method: "GET" })
      .then((response) => {
        setForm(toStaffSettingsFormState(response.data as StaffSettingsApiPayload));
      })
      .catch(() => notify("Failed to load staff settings", { type: "error" }))
      .finally(() => setLoading(false));
  }, [canView, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!canEdit) return;
    const validationError = validateStaffSettingsForm(form);
    if (validationError) {
      notify(validationError, { type: "warning" });
      return;
    }
    setSaving(true);
    try {
      const response = await callApi({
        location: "staffSettings",
        method: "PATCH",
        data: toStaffSettingsPatch(form),
      });
      setForm(toStaffSettingsFormState(response.data as StaffSettingsApiPayload));
      notify("Staff settings saved", { type: "success" });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const apiMessage = err?.response?.data?.message;
      const message = Array.isArray(apiMessage)
        ? apiMessage.join(", ")
        : apiMessage || err?.message || "Failed to save staff settings";
      notify(message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const dayBasisRequired = form.payrollEnabled && form.payrollDayBasis === "";
  const leaveFieldsRequired = form.leavePolicyEnabled;
  const validationError = validateStaffSettingsForm(form);

  return (
    <>
      <Title title="Staff settings" />
      <Box sx={{ p: 2, maxWidth: 640 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Staff settings
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Payroll and leave configuration.
        </Alert>

        {form.payrollSetupComplete ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Payroll setup is complete.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Payroll setup is incomplete. Enable payroll, choose a day basis, confirm professional
            tax, and configure leave policy (or leave it disabled) before onboarding salaried staff.
          </Alert>
        )}

        {!canEdit ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You can view these settings but do not have permission to edit them.
          </Alert>
        ) : null}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <SettingsSection title="Payroll">
            <SettingsToggleRow
              label="Payroll enabled"
              hint={STAFF_SETTINGS_TOGGLE_HINTS.payrollEnabled}
              checked={form.payrollEnabled}
              disabled={!canEdit || saving}
              onChange={(checked) => setForm((prev) => ({ ...prev, payrollEnabled: checked }))}
            />

            <TextField
              select
              label="Payroll day basis"
              size="small"
              value={form.payrollDayBasis}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  payrollDayBasis: e.target.value as PayrollDayBasis | "",
                }))
              }
              helperText={payrollDayBasisHelperText(form.payrollDayBasis)}
              disabled={!canEdit || saving}
              error={dayBasisRequired}
              required={form.payrollEnabled}
              slotProps={{
                select: {
                  renderValue: (selected) => {
                    const value = String(selected ?? "");
                    if (value === "") {
                      return "Not set";
                    }
                    return (
                      PAYROLL_DAY_BASIS_OPTIONS.find((option) => option.id === value)?.name ?? value
                    );
                  },
                },
              }}
            >
              <MenuItem value="">
                <em>Not set</em>
              </MenuItem>
              {PAYROLL_DAY_BASIS_OPTIONS.map((option) => (
                <MenuItem
                  key={option.id}
                  value={option.id}
                  sx={{
                    alignItems: "flex-start",
                    whiteSpace: "normal",
                    py: 1.25,
                  }}
                >
                  <ListItemText
                    primary={option.name}
                    secondary={option.helper}
                    slotProps={{
                      secondary: {
                        sx: { whiteSpace: "normal", mt: 0.5 },
                      },
                    }}
                  />
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Professional tax profile"
              size="small"
              value={form.professionalTaxProfile}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  professionalTaxProfile: e.target.value as ProfessionalTaxProfile,
                }))
              }
              helperText="Maharashtra slabs ship in v1. More states coming — choose None to opt out."
              disabled={!canEdit || saving}
            >
              {PROFESSIONAL_TAX_PROFILE_OPTIONS.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </SettingsSection>

          <SettingsSection title="Leave policy">
            {canEdit ? (
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={saving}
                  onClick={() => setForm((prev) => applyStandardLeavePolicyPreset(prev))}
                >
                  Apply standard preset
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  Sets 12 days / FY, 1 day monthly credit, advance allowed, annual cap, Casual +
                  Sick, join proration. You can change any field after.
                </Typography>
              </Box>
            ) : null}

            <SettingsToggleRow
              label="Leave policy enabled"
              hint={STAFF_SETTINGS_TOGGLE_HINTS.leavePolicyEnabled}
              checked={form.leavePolicyEnabled}
              disabled={!canEdit || saving}
              onChange={(checked) => setForm((prev) => ({ ...prev, leavePolicyEnabled: checked }))}
            />

            <TextField
              label="Annual paid leave entitlement (days)"
              size="small"
              type="number"
              inputProps={{ min: 0, step: 0.5 }}
              value={form.paidLeaveAnnualEntitlementDays}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  paidLeaveAnnualEntitlementDays: e.target.value,
                }))
              }
              helperText="Max paid working days per financial year (April–March)."
              disabled={!canEdit || saving || !leaveFieldsRequired}
              required={leaveFieldsRequired}
              error={
                leaveFieldsRequired &&
                (form.paidLeaveAnnualEntitlementDays.trim() === "" ||
                  Number(form.paidLeaveAnnualEntitlementDays) <= 0)
              }
            />

            <TextField
              label="Monthly leave credit (days)"
              size="small"
              type="number"
              inputProps={{ min: 0, step: 0.5 }}
              value={form.paidLeaveMonthlyCreditDays}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  paidLeaveMonthlyCreditDays: e.target.value,
                }))
              }
              helperText="Days credited to each employee each calendar month."
              disabled={!canEdit || saving || !leaveFieldsRequired}
              required={leaveFieldsRequired}
              error={
                leaveFieldsRequired &&
                (form.paidLeaveMonthlyCreditDays.trim() === "" ||
                  Number(form.paidLeaveMonthlyCreditDays) < 0)
              }
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Paid leave types
              </Typography>
              <FormGroup row>
                {PAID_LEAVE_TYPE_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.id}
                    control={
                      <Checkbox
                        checked={form.paidLeaveTypes.includes(option.id)}
                        onChange={(_, checked) =>
                          setForm((prev) => ({
                            ...prev,
                            paidLeaveTypes: togglePaidLeaveType(
                              prev.paidLeaveTypes,
                              option.id as PaidLeaveType,
                              checked
                            ),
                          }))
                        }
                        disabled={!canEdit || saving || !leaveFieldsRequired}
                      />
                    }
                    label={option.name}
                  />
                ))}
              </FormGroup>
              <Typography variant="caption" color="text.secondary">
                Which leave types consume the paid entitlement. Unpaid leave never does.
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <SettingsToggleRow
                label="Allow advance leave (before credited)"
                hint={STAFF_SETTINGS_TOGGLE_HINTS.paidLeaveAllowAdvance}
                checked={form.paidLeaveAllowAdvance}
                disabled={!canEdit || saving || !leaveFieldsRequired}
                onChange={(checked) =>
                  setForm((prev) => ({ ...prev, paidLeaveAllowAdvance: checked }))
                }
              />
              <SettingsToggleRow
                label="Cap paid leave at annual entitlement"
                hint={STAFF_SETTINGS_TOGGLE_HINTS.paidLeaveEnforceAnnualCap}
                checked={form.paidLeaveEnforceAnnualCap}
                disabled={!canEdit || saving || !leaveFieldsRequired}
                onChange={(checked) =>
                  setForm((prev) => ({ ...prev, paidLeaveEnforceAnnualCap: checked }))
                }
              />
              <SettingsToggleRow
                label="Prorate annual entitlement for mid-FY joins"
                hint={STAFF_SETTINGS_TOGGLE_HINTS.prorateEntitlementOnJoin}
                checked={form.prorateEntitlementOnJoin}
                disabled={!canEdit || saving || !leaveFieldsRequired}
                onChange={(checked) =>
                  setForm((prev) => ({ ...prev, prorateEntitlementOnJoin: checked }))
                }
              />
            </Box>
          </SettingsSection>

          {canEdit ? (
            <Box>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={saving || Boolean(validationError)}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </Box>
          ) : null}
        </Box>
      </Box>
    </>
  );
}
